import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { environment } from '../../../../environments/environment';
import { Location } from '../../../location/location.model';
import { LazyLocationPickerDirective } from '../../../shared/location-picker/lazy-location-picker.directive';
import { APIResponseWrapper } from '../../../models/common/api-response-wrapper';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { VehicleType } from '../../vehicle/params/vehicle-type';
import {
    FleetAvailabilityResponse,
    FleetAvailabilityRow,
    FleetFifoRecommendation,
    MobilizationDashboard,
    MobilizationDailyPlanning,
    MobilizationDailyPlanningRow,
    MobilizationDailyVehicleTypeRow,
    MobilizationRoadTrackingRow
} from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

interface PlanningFilterModel {
    dateRange: Date[] | null;
    vehicleTypeId?: number;
    destinationLocationId?: number;
    destination?: string;
    jobStatus?: string;
    includeOpenReleases: boolean;
    includeTracking: boolean;
}

@Component({
    selector: 'app-mobilization-planning',
    standalone: true,
    templateUrl: './mobilization-planning.component.html',
    styleUrls: ['./mobilization-planning.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        ChartModule,
        CheckboxModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        ProgressBarModule,
        SelectModule,
        LazyLocationPickerDirective,
        TableModule,
        TabsModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationPlanningComponent implements OnInit {
    private readonly tabKeys = ['availability', 'daily-planning', 'truck-type-dashboard', 'road-tracking'] as const;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    activeTab = 0;
    isLoading = false;
    planning?: MobilizationDailyPlanning;
    dashboard?: MobilizationDashboard;
    planningRows: MobilizationDailyPlanningRow[] = [];
    roadRows: MobilizationRoadTrackingRow[] = [];
    vehicleTypeRows: MobilizationDailyVehicleTypeRow[] = [];
    availability?: FleetAvailabilityResponse;
    driverStatusChartData: any = null;
    vehicleStatusChartData: any = null;
    utilizationChartData: any = null;
    driverAvailabilityRows: FleetAvailabilityRow[] = [];
    vehicleAvailabilityRows: FleetAvailabilityRow[] = [];
    fifoPairs: FleetFifoRecommendation[] = [];
    vehicleTypes: VehicleType[] = [];

    filters: PlanningFilterModel = {
        dateRange: this.nextSevenDaysRange(),
        includeOpenReleases: true,
        includeTracking: true
    };

    readonly jobStatusOptions = [
        { label: 'Accepted', value: 'JOB_DRIVER_ACCEPTED' },
        { label: 'Started Trip', value: 'DRIVER_STARTED_TRIP' },
        { label: 'En Route', value: 'DRIVER_EN_ROUTE' },
        { label: 'Delayed', value: 'DRIVER_DELAY_ON_TRIP' },
        { label: 'Reached Dropoff', value: 'DRIVER_REACHED_DROPOFF' },
        { label: 'Completion Acknowledged', value: 'JOB_COMPLETION_ACKNOWLEDGE' },
        { label: 'Delivered', value: 'DRIVER_JOB_COMPLETED' },
        { label: 'Cancelled', value: 'JOB_DRIVER_CANCELLED' }
    ];

    readonly availabilityChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 150,
        animation: false,
        plugins: {
            legend: {
                position: 'bottom'
            }
        }
    };

    readonly utilizationChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 150,
        animation: false,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                max: 100
            }
        }
    };

    constructor(
        private mobilizationService: MobilizationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Mobilization', routerLink: '/fleet/mobilization' },
            { label: 'Planning & Tracking' }
        ];
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes() ?? [];
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTabIndex(queryParams.get('tab'));
        });
        this.loadPlanning();
    }

    onTabChange(value: string | number): void {
        this.activeTab = Number(value);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTab] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    loadPlanning(): void {
        const companyId = this.companyId();
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        const range = this.resolvedDateRange();
        const destination = this.filters.destination?.trim() || undefined;
        this.isLoading = true;

        forkJoin({
            planning: this.mobilizationService.getDailyPlanning({
                owningCompanyId: companyId,
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                vehicleTypeId: this.filters.vehicleTypeId,
                destinationLocationId: this.filters.destinationLocationId,
                destination,
                includeOpenReleases: this.filters.includeOpenReleases,
                includeTracking: false
            }),
            dashboard: this.mobilizationService.getDashboard({
                owningCompanyId: companyId,
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                vehicleTypeId: this.filters.vehicleTypeId
            }),
            tracking: this.filters.includeTracking
                ? this.mobilizationService.getRoadTracking({
                    owningCompanyId: companyId,
                    dateFrom: range.dateFrom,
                    dateTo: range.dateTo,
                    vehicleTypeId: this.filters.vehicleTypeId,
                    destinationLocationId: this.filters.destinationLocationId,
                    destination,
                    jobStatus: this.filters.jobStatus
                })
                : of({
                    success: true,
                    message: '',
                    responseCode: '',
                    data: []
                } as APIResponseWrapper<MobilizationRoadTrackingRow[]>),
            availability: this.mobilizationService.getAvailability({
                owningCompanyId: companyId,
                dateFrom: range.dateFrom,
                dateTo: range.dateTo,
                vehicleTypeId: this.filters.vehicleTypeId
            })
        }).pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: response => {
                    this.planning = response.planning.data;
                    this.dashboard = response.dashboard.data;
                    this.planningRows = response.planning.data?.rows ?? [];
                    this.roadRows = response.tracking.data ?? [];
                    this.vehicleTypeRows = response.dashboard.data?.dailyVehicleTypeRows ?? [];
                    this.availability = response.availability.data;
                    this.driverStatusChartData = this.statusChartData(this.availability?.driverStatusBuckets);
                    this.vehicleStatusChartData = this.statusChartData(this.availability?.vehicleStatusBuckets);
                    const summary = this.availability?.summary;
                    this.utilizationChartData = {
                        labels: ['Drivers', 'Trucks'],
                        datasets: [{
                            data: [summary?.averageDriverUtilization ?? 0, summary?.averageVehicleUtilization ?? 0],
                            backgroundColor: ['#0284c7', '#0f766e'],
                            borderRadius: 6
                        }]
                    };
                    this.driverAvailabilityRows = response.availability.data?.driverRows ?? [];
                    this.vehicleAvailabilityRows = response.availability.data?.vehicleRows ?? [];
                    this.fifoPairs = response.availability.data?.fifoPairs ?? [];
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilization planning.' });
                }
            });
    }

    clearFilters(): void {
        this.filters = {
            dateRange: this.nextSevenDaysRange(),
            includeOpenReleases: true,
            includeTracking: true
        };
        this.loadPlanning();
    }

    metrics(): PageMetric[] {
        const demand = this.sum(this.planningRows, row => row.demandTruckCount);
        const capacity = this.sum(this.planningRows, row => row.mobilizedCapacityCount);
        const openReleases = this.sum(this.planningRows, row => row.openReleaseCount);
        const delayed = this.sum(this.planningRows, row => row.delayedCount);
        const delivered = this.sum(this.planningRows, row => row.deliveredCount);
        const gap = demand - capacity;
        const availabilitySummary = this.availability?.summary;

        return [
            { label: 'Demand Trucks', value: demand, hint: 'Deliveries, shipments, releases', tone: 'info' },
            { label: 'Mobilized Capacity', value: capacity, hint: 'Approved and active assignments', tone: capacity >= demand ? 'success' : 'warn' },
            { label: 'Capacity Gap', value: gap, hint: 'Demand minus mobilized capacity', tone: gap > 0 ? 'danger' : 'success' },
            { label: 'Free Drivers', value: availabilitySummary?.freeDrivers ?? 0, hint: 'Ready for FIFO allocation', tone: 'success' },
            { label: 'Free Trucks', value: availabilitySummary?.freeVehicles ?? 0, hint: 'Ready for FIFO allocation', tone: 'success' },
            { label: 'Open Releases', value: openReleases, hint: 'Unplanned transport releases', tone: openReleases ? 'warn' : 'default' },
            { label: 'In Transit', value: this.sum(this.planningRows, row => row.inTransitCount), hint: 'Delivery jobs on the road', tone: 'info' },
            { label: 'Delayed', value: delayed, hint: 'Jobs in delay status', tone: delayed ? 'danger' : 'success' },
            { label: 'Delivered', value: delivered, hint: 'Completed delivery jobs', tone: 'success' }
        ];
    }

    activeFilterCount(): number {
        return [
            this.filters.dateRange?.[0],
            this.filters.dateRange?.[1],
            this.filters.vehicleTypeId,
            this.filters.destinationLocationId,
            this.filters.destination,
            this.filters.jobStatus,
            this.filters.includeOpenReleases ? 'open' : '',
            this.filters.includeTracking ? 'tracking' : ''
        ].filter(Boolean).length;
    }

    resultText(): string {
        return `${this.planningRows.length} planning rows, ${this.roadRows.length} tracking rows, ${this.driverAvailabilityRows.length + this.vehicleAvailabilityRows.length} availability rows`;
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    dateTimeLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y HH:mm', 'en-US') : '-';
    }

    numberLabel(value?: number): number {
        return value ?? 0;
    }

    amountLabel(value?: number): string {
        return `${value ?? 0} ${this.dashboard?.summary?.salaryCurrencyCode || 'SAR'}`;
    }

    destinationLabel(row: MobilizationDailyPlanningRow | MobilizationRoadTrackingRow): string {
        return row.destinationLabel || (row.destinationLocationId ? `Location #${row.destinationLocationId}` : 'Unassigned destination');
    }

    vehicleTypeLabel(row: MobilizationDailyPlanningRow | MobilizationRoadTrackingRow | MobilizationDailyVehicleTypeRow): string {
        return row.vehicleTypeName || (row.vehicleTypeId ? `Type ${row.vehicleTypeId}` : 'Unspecified');
    }

    locationLabel(location: Location): string {
        return [location.externalLocationReference, location.name || location.shortDescription || location.longDescription]
            .filter(Boolean)
            .join(' - ') || `Location #${location.id}`;
    }

    jobStatusLabel(status?: string): string {
        if (!status) {
            return '-';
        }
        return status.toLowerCase().split('_').map(value => value.charAt(0).toUpperCase() + value.slice(1)).join(' ');
    }

    capacitySeverity(row: MobilizationDailyPlanningRow): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        const gap = row.capacityGap ?? 0;
        if (gap > 0) {
            return 'danger';
        }
        if (gap < 0) {
            return 'success';
        }
        return 'info';
    }

    trackingSeverity(state?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (state) {
            case 'DELIVERED':
                return 'success';
            case 'DELAYED':
            case 'CANCELLED':
                return 'danger';
            case 'IN_TRANSIT':
                return 'info';
            case 'ASSIGNED':
                return 'warning';
            case 'OPEN':
                return 'secondary';
            default:
                return 'secondary';
        }
    }

    availabilitySeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return 'success';
            case 'BUSY':
                return 'warning';
            case 'MAINTENANCE_BLOCKED':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    availabilityStatusLabel(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return 'Idle';
            case 'BUSY':
                return 'Busy';
            case 'MAINTENANCE_BLOCKED':
                return 'Blocked';
            default:
                return this.statusText(status);
        }
    }

    availabilityStatusClass(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return 'availability-tag-idle';
            case 'BUSY':
                return 'availability-tag-busy';
            case 'MAINTENANCE_BLOCKED':
                return 'availability-tag-blocked';
            default:
                return 'availability-tag-neutral';
        }
    }

    utilizationLabel(value?: number): string {
        return `${Math.round((value ?? 0) * 10) / 10}%`;
    }

    utilizationHelpText(): string {
        return 'Utilization uses 24 available hours per day in the selected range. Busy hours come from planned delivery windows and project-backed mobilization dates.';
    }

    availabilityWorkLabel(row: FleetAvailabilityRow): string {
        return row.currentWork?.context || row.availabilityReason || 'No active work';
    }

    nextFreeLabel(row: FleetAvailabilityRow): string {
        if (row.availabilityStatus === 'FREE') {
            return 'Now';
        }
        return this.dateTimeLabel(row.nextFreeAt);
    }

    availableAtLabel(row: FleetAvailabilityRow): string {
        const cityCountry = [row.availableCityName, row.availableCountryName].filter(Boolean).join(', ');
        return cityCountry || row.availableLocationLabel || 'Location not available';
    }

    openAvailabilityWork(row: FleetAvailabilityRow): void {
        const work = row.currentWork;
        if (!work) {
            return;
        }
        if (work.deliveryId) {
            this.router.navigate(['/shipment/delivery/view', work.deliveryId]);
            return;
        }
        if (work.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', work.mobilizationId]);
        }
    }

    openShipmentFromWork(row: FleetAvailabilityRow): void {
        const shipmentId = row.currentWork?.shipmentId;
        if (shipmentId) {
            this.router.navigate(['/shipment/view', shipmentId]);
        }
    }

    openDriver(row: FleetAvailabilityRow | FleetFifoRecommendation): void {
        const driverId = (row as FleetAvailabilityRow).resourceId ?? (row as FleetFifoRecommendation).driverId;
        if (driverId) {
            this.router.navigate(['/fleet/driver/view', driverId]);
        }
    }

    openVehicle(row: FleetAvailabilityRow | FleetFifoRecommendation): void {
        const vehicleId = (row as FleetAvailabilityRow).resourceId ?? (row as FleetFifoRecommendation).vehicleId;
        if (vehicleId) {
            this.router.navigate(['/fleet/vehicle/view', vehicleId]);
        }
    }

    openDelivery(row: MobilizationRoadTrackingRow): void {
        if (row.deliveryId) {
            this.router.navigate(['/shipment/delivery/view', row.deliveryId]);
        }
    }

    openMobilization(row: MobilizationRoadTrackingRow): void {
        if (row.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', row.mobilizationId]);
        }
    }

    openEta(row: MobilizationRoadTrackingRow): void {
        if (!row.etaUrl) {
            return;
        }
        const apiRoot = environment.restApiUrl.replace(/\/api\/?$/, '');
        window.open(`${apiRoot}${row.etaUrl}`, '_blank', 'noopener');
    }

    private statusChartData(buckets: { label?: string; count?: number }[] | undefined): any {
        const rows = buckets?.length ? buckets : [{ label: 'No data', count: 0 }];
        return {
            labels: rows.map(row => this.statusText(row.label)),
            datasets: [{
                data: rows.map(row => row.count ?? 0),
                backgroundColor: rows.map(row => this.availabilityStatusColor(row.label))
            }]
        };
    }

    private availabilityStatusColor(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'FREE':
                return '#10b981';
            case 'BUSY':
                return '#f59e0b';
            case 'MAINTENANCE_BLOCKED':
                return '#ef4444';
            default:
                return '#64748b';
        }
    }

    private statusText(value?: string): string {
        if (!value) {
            return '-';
        }
        return value.toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
    }

    private resolvedDateRange(): { dateFrom: Date; dateTo: Date } {
        const fallback = this.nextSevenDaysRange();
        const from = this.filters.dateRange?.[0] ?? fallback[0];
        const to = this.filters.dateRange?.[1] ?? from;
        return { dateFrom: from, dateTo: to };
    }

    private nextSevenDaysRange(): Date[] {
        const today = new Date();
        const end = new Date();
        end.setDate(today.getDate() + 6);
        return [today, end];
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private sum<T>(rows: T[], resolver: (row: T) => number | undefined): number {
        return rows.reduce((total, row) => total + (resolver(row) ?? 0), 0);
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(key => key === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }
}
